import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  ChevronRight,
  ChevronsUpDown,
  LogOut,
  Menu,
  Moon,
  Pin,
  Plus,
  SlidersHorizontal,
  Sun,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { useThemeStore } from '../stores/themeStore';
import { useUnreadNotifications } from './NotificationBell';
import { MdsLogo } from './MdsLogo';
import { cn } from '../utils/cn';
import {
  buildNavSections,
  activeItem,
  navLabel,
  SECTION_ICONS,
  type NavSectionKey,
} from '../config/navPresentation';

interface AppShellProps {
  children: ReactNode;
  /**
   * 'portal' — the B2B self-ordering portal in this same layout (feedback 3.4, 2026-10-22: "the
   * same layout as the admin, with the menu column on the left"). The menu column is shared; the
   * portal brings its own appbar (search, vehicle, cart, account), a breadcrumb under it, and its
   * MDS portal styling around the page.
   */
  variant?: 'staff' | 'portal';
  /** Replaces the default appbar content (logo, page name, bell, theme). */
  appbar?: ReactNode;
  /** Drawn between the appbar and the page (the portal's breadcrumb). */
  subbar?: ReactNode;
  /** Extra path prefixes laid out full-bleed (the portal's catalogue). */
  fullBleedPaths?: string[];
  /** A count shown on a menu entry, by path (the portal's returns to send back). */
  badges?: Record<string, number>;
}

// Pre-redesign key: '1' meant "collapsed". Read once so a seller who had folded the menu keeps
// the rail; the pinned flag replaces it from then on.
const LEGACY_COLLAPSE_KEY = 'mecadepot.sidebar.collapsed';
const PIN_KEY = 'mecadepot.sidebar.pinned';
const OPEN_KEY = 'mecadepot.sidebar.openGroups';

// Pulled out of their sections and given fixed homes, as in the Comptoir prototype: the sale
// entry becomes the primary button at the top, settings sits with sign-out at the bottom.
const NEW_SALE_PATH = '/pos/sale';
const SETTINGS_PATH = '/admin/settings';

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / blocked storage: the preference just isn't remembered */
  }
}

export function AppShell({
  children,
  variant = 'staff',
  appbar,
  subbar,
  fullBleedPaths = [],
  badges = {},
}: AppShellProps) {
  const portal = variant === 'portal';
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const caps = useCapabilitiesStore((s) => s.data);
  const clearCaps = useCapabilitiesStore((s) => s.clear);
  const { pathname } = useLocation();

  const [pinned, setPinned] = useState<boolean>(() => {
    const saved = readJson<boolean | null>(PIN_KEY, null);
    if (saved !== null) return saved;
    try {
      return localStorage.getItem(LEGACY_COLLAPSE_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    readJson(OPEN_KEY, { sales: true })
  );
  // Hover "peek": the rail widens OVER the content without moving it.
  const [peek, setPeek] = useState(false);
  // Phone-width drawer.
  const [mobileOpen, setMobileOpen] = useState(false);
  const peekTimer = useRef<number>();
  // The account menu (Profil · Paramètres · Thème · Déconnexion), opened from the bottom entry.
  const [accountOpen, setAccountOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ left: 0, bottom: 0 });
  const accountBtn = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const theme = useThemeStore((s) => s.resolved);
  const toggleTheme = useThemeStore((s) => s.toggle);
  // The portal keeps its bell in its own bar (which runs the stream); staff read it here.
  const unread = useUnreadNotifications(variant !== 'portal');
  useEffect(() => {
    if (!accountOpen || !accountBtn.current) return;
    // Beside the column (measured again once it has widened), bottom-aligned with the entry.
    const place = () => {
      if (!accountBtn.current) return;
      const r = accountBtn.current.getBoundingClientRect();
      const nav = navRef.current?.getBoundingClientRect();
      setMenuPos({
        left: Math.round((nav?.right ?? r.right) + 8),
        bottom: Math.max(8, Math.round(window.innerHeight - r.bottom)),
      });
    };
    place();
    const id = window.setTimeout(place, 180); // after the width transition
    return () => window.clearTimeout(id);
  }, [accountOpen, peek]);

  useEffect(() => writeStorage(PIN_KEY, JSON.stringify(pinned)), [pinned]);
  useEffect(() => writeStorage(OPEN_KEY, JSON.stringify(openGroups)), [openGroups]);
  useEffect(() => {
    setMobileOpen(false);
    setPeek(false);
    setAccountOpen(false);
  }, [pathname]);
  useEffect(() => () => window.clearTimeout(peekTimer.current), []);

  if (!user || !caps) return <>{children}</>;

  const items = caps.navigation.items;
  const newSale = items.find((it) => it.path === NEW_SALE_PATH);
  const settings = items.find((it) => it.path === SETTINGS_PATH);
  const sections = buildNavSections(
    items.filter((it) => it.path !== NEW_SALE_PATH && it.path !== SETTINGS_PATH)
  );
  const active = activeItem(items, pathname);
  // Order-creation screens are laid out as a full-bleed 3-column catalogue (filters |
  // results | cart) and manage their own padding, so skip the padded content wrapper.
  const fullBleed = ['/pos/sale', '/orders/new', ...fullBleedPaths].some((p) =>
    pathname.startsWith(p)
  );
  // Counter sellers spend most of their day on the order screen, and the receiving tablet
  // (spec of 2026-09-23 §10) must show the destination card without scrolling at 1280 × 800 —
  // both force the icon rail. Hovering it still peeks the full menu over the content, and the
  // saved pin preference is untouched and restored when they navigate away.
  const tablet = pathname.startsWith('/warehouse/reception');
  const forcedRail = fullBleed || tablet;
  const docked = pinned && !forcedRail;
  const expanded = docked || peek || mobileOpen;

  const signOut = () => {
    clearCaps();
    logout();
  };

  const onEnter = () => {
    window.clearTimeout(peekTimer.current);
    peekTimer.current = window.setTimeout(() => setPeek(true), 120);
  };
  const onLeave = () => {
    window.clearTimeout(peekTimer.current);
    // The account menu sits beside the column: leaving towards it must not fold the column.
    if (accountOpen) return;
    setPeek(false);
  };

  const toggleGroup = (key: NavSectionKey, containsActive: boolean) => {
    // In the rail, a group click opens the column first (and the group with it) rather than
    // navigating somewhere the seller can't see yet.
    if (!expanded) {
      setPeek(true);
      setOpenGroups((g) => ({ ...g, [key]: true }));
      return;
    }
    setOpenGroups((g) => ({ ...g, [key]: !(g[key] || containsActive) }));
  };

  const name = (user.email ?? user.sub).split('@')[0].replace(/[._]/g, ' ');
  const initials = name.slice(0, 2).toUpperCase();
  const roleLabel = t(`roles.${user.role}`, { defaultValue: user.role });

  return (
    <div className="h-screen overflow-hidden flex bg-canvas text-foreground">
      {/* Reserves the rail/docked width in the flow; the nav itself is fixed so a peek can
          overlay the content instead of reflowing it. Hidden at phone width (drawer instead). */}
      <div
        aria-hidden="true"
        className={cn(
          'hidden md:block shrink-0 transition-[width] duration-150',
          docked ? 'w-[248px]' : 'w-[76px]'
        )}
      />

      {mobileOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/35 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav
        ref={navRef}
        aria-label={t('nav.mainNav')}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        className={cn(
          'fixed inset-y-0 left-0 flex flex-col gap-1 overflow-hidden border-r bg-card py-3 transition-[width,box-shadow] duration-150',
          expanded ? 'w-[248px] px-2.5' : 'w-[76px] px-2',
          !docked && peek && 'shadow-[10px_0_34px_rgba(10,20,40,.18)]',
          // Phone: off-canvas drawer.
          mobileOpen ? 'z-[70] flex w-[min(290px,85vw)]' : 'z-[45] max-md:hidden'
        )}
      >
        {/* Brand at the top of the column (2026-10-23 layout): the logo leaves the top bar, the
            pin sits beside it. */}
        <div
          className={cn(
            'mds mb-2 flex h-10 items-center',
            expanded ? 'justify-between pl-1.5' : 'justify-center'
          )}
        >
          <Link to="/" aria-label="MecaRadar" className="flex items-center">
            <MdsLogo wordmark={expanded} />
          </Link>
          {expanded && !forcedRail && !mobileOpen && (
            <button
              type="button"
              onClick={() => setPinned((p) => !p)}
              aria-pressed={pinned}
              title={pinned ? t('nav.unpin') : t('nav.pin')}
              className={cn(
                'grid h-[30px] w-[30px] shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent',
                pinned && 'text-primary'
              )}
            >
              <Pin className="h-4 w-4" />
            </button>
          )}
        </div>

        {newSale && (
          <Link
            to={newSale.path}
            title={navLabel(newSale.label, t)}
            className={cn(
              'mb-1.5 flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-primary bg-primary font-bold text-primary-foreground hover:bg-primary/90',
              expanded ? 'px-4' : 'px-0'
            )}
          >
            <Plus className="h-4 w-4 shrink-0" strokeWidth={2.4} />
            {expanded && <span className="truncate">{navLabel(newSale.label, t)}</span>}
          </Link>
        )}

        {/* Navigation */}
        <div className="-mx-1 flex flex-1 flex-col gap-px overflow-y-auto overflow-x-hidden px-1">
          {sections.map((section) => {
            const shortLabel = portal
              ? t(`nav.portalShort.${section.key}`, {
                  defaultValue: t(`nav.short.${section.key}`),
                })
              : t(`nav.short.${section.key}`);
            // A one-item section (e.g. Dashboard) is a plain top-level entry, not a group.
            if (section.items.length === 1) {
              const item = section.items[0];
              return (
                <NavEntry
                  key={section.key}
                  to={item.path}
                  icon={item.icon}
                  label={navLabel(item.label, t)}
                  short={shortLabel}
                  active={active?.path === item.path}
                  expanded={expanded}
                  badge={badges[item.path]}
                />
              );
            }
            const containsActive = section.items.some((it) => it.path === active?.path);
            const isOpen = !!openGroups[section.key] || containsActive;
            const GroupIcon = SECTION_ICONS[section.key];
            // A garage reads its own words: "Ventes" is the shop's view of what it buys.
            const label = portal
              ? t(`nav.portalSections.${section.key}`, {
                  defaultValue: t(`nav.sections.${section.key}`),
                })
              : t(`nav.sections.${section.key}`);
            // A closed group still says that something inside it is waiting.
            const groupBadge = section.items.reduce((n, it) => n + (badges[it.path] ?? 0), 0);
            return (
              <div key={section.key} className="flex flex-col gap-px">
                <button
                  type="button"
                  onClick={() => toggleGroup(section.key, containsActive)}
                  aria-expanded={expanded ? isOpen : undefined}
                  title={label}
                  className={cn(
                    'relative flex w-full items-center rounded-md text-left text-[14.5px] text-foreground hover:bg-accent',
                    expanded
                      ? 'mt-1 gap-2.5 p-2 font-semibold'
                      : 'flex-col justify-center gap-1 px-0 py-2',
                    !expanded && containsActive && 'bg-primary-soft text-primary'
                  )}
                >
                  <GroupIcon
                    className={cn(
                      'h-[19px] w-[19px] shrink-0',
                      !expanded && containsActive ? 'text-primary' : 'text-muted-foreground'
                    )}
                    strokeWidth={1.7}
                  />
                  {groupBadge > 0 && !(expanded && isOpen) && <CountBadge n={groupBadge} />}
                  {expanded ? (
                    <>
                      <span className="min-w-0 flex-1 truncate">{label}</span>
                      <ChevronRight
                        className={cn(
                          'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                          isOpen && 'rotate-90'
                        )}
                        strokeWidth={2}
                      />
                    </>
                  ) : (
                    <span className="block text-center text-[11px] leading-tight">
                      {shortLabel}
                    </span>
                  )}
                </button>
                {expanded && isOpen && (
                  <div className="ml-[17px] flex flex-col gap-px border-l pl-2.5">
                    {section.items.map((item) => (
                      <NavEntry
                        key={item.path}
                        to={item.path}
                        label={navLabel(item.label, t)}
                        active={active?.path === item.path}
                        expanded
                        sub
                        badge={badges[item.path]}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-0.5 border-t pt-1.5">
          {/* The bell moved from the top bar into the column (the portal keeps its own in its bar). */}
          {!portal && (
            <NavEntry
              to="/notifications"
              icon={Bell}
              label={t('notifications.title')}
              short={t('nav.short.notifications')}
              active={pathname.startsWith('/notifications')}
              expanded={expanded}
              badge={unread}
            />
          )}
          {/* One account entry: who is signed in, opening Profil · Paramètres · Thème · Déconnexion. */}
          <button
            ref={accountBtn}
            type="button"
            onClick={() => setAccountOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            title={`${name} · ${roleLabel}`}
            className={cn(
              'relative flex w-full items-center rounded-md text-left text-[14.5px] text-foreground hover:bg-accent',
              expanded ? 'gap-2.5 p-2' : 'flex-col justify-center gap-1 px-0 py-2',
              (accountOpen || pathname.startsWith('/profile')) && 'bg-accent'
            )}
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
              {initials}
            </span>
            {expanded ? (
              <>
                <span className="min-w-0 flex-1">
                  <b className="block truncate text-sm capitalize">{name}</b>
                  <span className="block truncate text-xs text-muted-foreground">{roleLabel}</span>
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </>
            ) : (
              <span className="block text-center text-[11px] leading-tight">
                {t('nav.profileEntry')}
              </span>
            )}
          </button>
        </div>
        {accountOpen && (
          <>
            <div
              className="fixed inset-0 z-[80]"
              onClick={() => {
                setAccountOpen(false);
                setPeek(false);
              }}
              aria-hidden="true"
            />
            <div
              role="menu"
              className="fixed z-[90] w-64 overflow-hidden rounded-lg border bg-card text-sm shadow-xl"
              style={{ left: menuPos.left, bottom: menuPos.bottom }}
            >
              <div className="flex items-center gap-3 border-b px-3 py-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {initials}
                </span>
                <span className="min-w-0">
                  <b className="block truncate capitalize">{name}</b>
                  <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
                  <span className="block truncate text-xs text-muted-foreground">{roleLabel}</span>
                </span>
              </div>
              <div className="p-1">
                <MenuLink
                  to="/profile"
                  icon={UserRound}
                  label={t('nav.profile')}
                  onPick={() => setAccountOpen(false)}
                />
                {settings && (
                  <MenuLink
                    to={settings.path}
                    icon={SlidersHorizontal}
                    label={navLabel(settings.label, t)}
                    onPick={() => setAccountOpen(false)}
                  />
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={toggleTheme}
                  className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 hover:bg-accent"
                >
                  {theme === 'dark' ? (
                    <Sun className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Moon className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="flex-1 text-left">
                    {theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')}
                  </span>
                </button>
              </div>
              <div className="border-t p-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={signOut}
                  className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="h-4 w-4" />
                  {t('nav.signOut')}
                </button>
              </div>
            </div>
          </>
        )}
      </nav>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — MDS counter appbar */}
        {/* Staff pages: phone only — on a desktop the page's own header names it and the menu
            column carries the rest, so the bar was an empty strip above every page. */}
        <div
          className={cn(
            'mds sticky top-0 z-20',
            portal ? 'is-portail' : 'is-comptoir',
            !portal && !appbar && 'md:hidden'
          )}
        >
          {/* The portal's bar carries the parts search, which wraps onto its own row at phone
              width — so it grows instead of being clipped at the fixed 56px. */}
          <header className={cn('appbar', portal ? 'min-h-14' : 'h-14')}>
            {/* Phone only. The wrapper carries `md:hidden` because `.mds .appbar .iconbutton` sets
                display and out-specifies a utility on the button itself. */}
            <span className="md:hidden">
              <button
                type="button"
                className="iconbutton grid place-items-center rounded-md"
                onClick={() => setMobileOpen(true)}
                aria-label={t('nav.openMenu')}
              >
                <Menu className="h-4 w-4" />
              </button>
            </span>
            {appbar ?? (
              <>
                {/* The logo, the bell and the theme live in the menu column now; on a phone the
                    column is a drawer, so the bar keeps the logo there. */}
                <span className="md:hidden">
                  <MdsLogo />
                </span>
                <div className="flex min-w-0 items-center text-base">
                  <span className="strong truncate">
                    {active
                      ? navLabel(active.label, t)
                      : pathname.startsWith('/notifications')
                        ? t('notifications.title')
                        : pathname.startsWith('/profile')
                          ? t('nav.profile')
                          : ''}
                  </span>
                </div>
              </>
            )}
          </header>
        </div>

        <main className="flex-1 overflow-auto">
          {portal ? (
            /* The portal's pages are MDS portal pages: they need the `.mds.is-portail` ancestor,
               the breadcrumb above them, and the centred `.page` (or the full-bleed builder). */
            <div
              className={cn(
                'mds is-portail flex min-h-full flex-col',
                !fullBleed && 'minw-portrait'
              )}
              style={{ background: 'var(--mds-canvas)' }}
            >
              {subbar}
              {fullBleed ? (
                <div className="mds-bleed">{children}</div>
              ) : (
                <div className="page">{children}</div>
              )}
            </div>
          ) : fullBleed ? (
            children
          ) : (
            <div className={cn('w-full', tablet ? 'p-3' : 'px-4 pt-4 pb-10 md:px-5')}>
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function NavEntry({
  to,
  icon: Icon,
  label,
  short,
  active,
  expanded,
  sub = false,
  badge,
}: {
  to: string;
  icon?: LucideIcon;
  label: string;
  short?: string;
  active: boolean;
  expanded: boolean;
  sub?: boolean;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      title={label}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex w-full items-center rounded-md text-foreground hover:bg-accent',
        sub ? 'px-2 py-1.5 text-sm' : 'text-[14.5px]',
        !sub && (expanded ? 'gap-2.5 p-2' : 'flex-col justify-center gap-1 px-0 py-2'),
        active && 'bg-primary-soft font-bold text-primary hover:bg-primary-soft'
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            'h-[19px] w-[19px] shrink-0',
            active ? 'text-primary' : 'text-muted-foreground'
          )}
          strokeWidth={1.7}
        />
      )}
      {expanded ? (
        <span className="min-w-0 flex-1 truncate">{label}</span>
      ) : (
        <span className="block text-center text-[11px] leading-tight">{short ?? label}</span>
      )}
      {!!badge && badge > 0 && <CountBadge n={badge} inline={expanded} />}
    </Link>
  );
}

/** A small count: inline at the end of an open entry, pinned to the corner in the rail. */
function CountBadge({ n, inline = false }: { n: number; inline?: boolean }) {
  return (
    <span
      className={cn(
        'grid h-[18px] min-w-[18px] place-items-center rounded-full bg-destructive px-1 text-[11px] font-bold text-white',
        inline ? 'shrink-0' : 'absolute right-1 top-1'
      )}
    >
      {n}
    </span>
  );
}

/** One entry of the account menu. */
function MenuLink({
  to,
  icon: Icon,
  label,
  onPick,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  onPick: () => void;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onPick}
      className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-foreground hover:bg-accent"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      {label}
    </Link>
  );
}
